import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  ChevronLeft, Trash2, Plus, Minus, Gift, Truck, RotateCcw, Lock, Check, X,
} from "lucide-react";
import { AnnouncementBar } from "../components/AnnouncementBar";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Eyebrow, SerifH } from "../components/shared";
import { useCart } from "../context/CartContext";
import { PRODUCTS } from "../data/content";
import { trackEvent } from "../lib/analytics";
import {
  cartLinesToItems,
  toAnalyticsItem,
  parsePriceEur,
  CURRENCY,
} from "../lib/catalog";

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (n) => `€${n.toLocaleString("en-DE")}`;

function LineItem({ item }) {
  const { updateQty, removeItem, updateGift } = useCart();
  const [editGift, setEditGift] = useState(false);
  const [note, setNote] = useState(item.gift || "");
  const p = item.product;
  const save = () => { updateGift(item.id, note.trim()); setEditGift(false); };
  const remove = () => {
    trackEvent("remove_from_cart", {
      currency: CURRENCY,
      value: parsePriceEur(p.price) * item.qty,
      items: [toAnalyticsItem(p, item.qty, undefined, item.size)],
    });
    removeItem(item.id);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, transition: { duration: 0.3 } }}
      data-testid={`cart-line-${item.slug}`}
      className="flex gap-4 md:gap-6 py-6 border-b border-[#0C111C]/10"
    >
      <Link to={`/product/${p.slug}`} className="w-24 md:w-32 shrink-0 aspect-[558/1000] bg-[#EDE2CE] overflow-hidden block">
        <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
      </Link>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link to={`/product/${p.slug}`} className="text-lg md:text-xl leading-tight hover:text-[#C8A96A] transition-colors"
              style={{ fontFamily: "Fraunces, serif" }}>
              {p.name}
            </Link>
            <p className="mt-1 text-xs md:text-sm text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
              {p.print} · Size {item.size}
            </p>
          </div>
          <p className="text-lg md:text-xl text-[#0C111C] shrink-0" style={{ fontFamily: "Fraunces, serif" }}>
            {eur(item.lineTotal)}
          </p>
        </div>

        {/* Gift note */}
        <div className="mt-3">
          {editGift ? (
            <div className="flex gap-2 items-start">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 80))}
                rows={2}
                maxLength={80}
                data-testid={`cart-line-gift-input-${item.slug}`}
                className="flex-1 text-sm italic bg-transparent border-b border-[#0C111C]/25 focus:border-[#0C182E] outline-none py-1 resize-none"
                style={{ fontFamily: "Fraunces, serif" }}
              />
              <button type="button" onClick={save} data-testid={`cart-line-gift-save-${item.slug}`}
                className="text-xs tracking-[0.2em] uppercase text-[#C8A96A] hover:underline"
                style={{ fontFamily: "Inter, sans-serif" }}>Save</button>
            </div>
          ) : item.gift ? (
            <button type="button" onClick={() => setEditGift(true)}
              data-testid={`cart-line-gift-edit-${item.slug}`}
              className="flex items-start gap-2 text-left group">
              <Gift size={14} className="mt-0.5 text-[#C8A96A] shrink-0" />
              <span className="text-sm italic text-[#0C111C] group-hover:text-[#C8A96A] transition-colors"
                style={{ fontFamily: "Fraunces, serif" }}>
                "{item.gift}"
              </span>
            </button>
          ) : (
            <button type="button" onClick={() => setEditGift(true)}
              data-testid={`cart-line-gift-add-${item.slug}`}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#0C111C]/60 hover:text-[#C8A96A]"
              style={{ fontFamily: "Inter, sans-serif" }}>
              <Gift size={12} /> Add gift note
            </button>
          )}
        </div>

        {/* Qty + Remove */}
        <div className="mt-auto pt-4 flex items-center justify-between">
          <div className="flex items-center border border-[#0C111C]/20">
            <button type="button" aria-label="Decrease" data-testid={`cart-line-dec-${item.slug}`}
              onClick={() => updateQty(item.id, item.qty - 1)}
              className="w-9 h-9 hover:bg-[#0C111C]/5"><Minus size={14} className="mx-auto" /></button>
            <span className="w-8 text-center text-sm" style={{ fontFamily: "Inter, sans-serif" }}>{item.qty}</span>
            <button type="button" aria-label="Increase" data-testid={`cart-line-inc-${item.slug}`}
              onClick={() => updateQty(item.id, item.qty + 1)}
              className="w-9 h-9 hover:bg-[#0C111C]/5"><Plus size={14} className="mx-auto" /></button>
          </div>
          <button type="button" onClick={remove}
            data-testid={`cart-line-remove-${item.slug}`}
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-[#0C111C]/60 hover:text-[#B95841]"
            style={{ fontFamily: "Inter, sans-serif" }}>
            <Trash2 size={12} /> Remove
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <div className="max-w-md mx-auto text-center py-24" data-testid="cart-empty">
      <SerifH size="clamp(28px, 3vw, 44px)">Your bag is empty.</SerifH>
      <p className="mt-4 text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
        Nothing to fold into brass foil just yet. Head to the collection and pick one that finds you.
      </p>
      <Link to="/collection" data-testid="cart-empty-cta"
        className="mt-8 inline-flex items-center gap-2 bg-[#0C182E] hover:bg-[#0C111C] text-[#F4EDE2] px-8 h-14 text-sm tracking-[0.22em] uppercase transition-colors"
        style={{ fontFamily: "Inter, sans-serif" }}>
        Explore the collection →
      </Link>
    </div>
  );
}

export function CartPage() {
  const cart = useCart();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState("");
  const [promoInput, setPromoInput] = useState(cart.promoCode);

  // view_cart on mount + whenever line count changes (empty cart still fires once).
  useEffect(() => {
    trackEvent("view_cart", {
      currency: CURRENCY,
      value: cart.subtotal,
      items: cartLinesToItems(cart.items),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.count]);

  const applyPromo = () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) { cart.setPromoCode(""); return; }
    if (code !== "YABA10") { setError("That code isn't valid."); return; }
    cart.setPromoCode(code);
    setError("");
  };

  const checkout = async () => {
    if (!cart.items.length || checkingOut) return;
    setCheckingOut(true);
    setError("");
    trackEvent("begin_checkout", {
      currency: CURRENCY,
      value: cart.total,
      coupon: cart.promoCode || undefined,
      items: cartLinesToItems(cart.items),
    });
    try {
      const { data } = await axios.post(`${API}/api/payments/checkout`, {
        items: cart.items.map((i) => ({
          slug: i.slug, quantity: i.qty, size: i.size, gift_note: i.gift || null,
        })),
        origin_url: window.location.origin,
        promo_code: cart.promoCode || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError("Checkout failed. Please try again.");
      setCheckingOut(false);
    }
  };

  const progressPct = Math.min(100, Math.round((cart.subtotal / cart.shippingThreshold) * 100));
  const related = PRODUCTS.filter((p) => !cart.items.find((i) => i.slug === p.slug)).slice(0, 3);

  return (
    <div className="bg-[#F4EDE2] text-[#0C111C] min-h-screen" data-testid="cart-page">
      <AnnouncementBar />
      <Nav />
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 pt-10 md:pt-14 pb-4">
        <Link to="/collection" data-testid="cart-back"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#0C111C]/60 hover:text-[#C8A96A]"
          style={{ fontFamily: "Inter, sans-serif" }}>
          <ChevronLeft size={14} /> Keep shopping
        </Link>
      </div>

      <section className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] gap-12 lg:gap-20">
          {/* LEFT — line items */}
          <div>
            <Eyebrow>Your bag</Eyebrow>
            <SerifH className="mt-4" size="clamp(36px, 4.8vw, 72px)">
              {cart.count === 0 ? "Empty and quiet." : cart.count === 1 ? "One jellaba." : `${cart.count} jellabas.`}
            </SerifH>

            {cart.items.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                {/* Free shipping progress */}
                <div className="mt-8" data-testid="cart-shipping-progress">
                  <div className="flex items-center justify-between text-[11px] tracking-[0.22em] uppercase mb-2"
                    style={{ fontFamily: "Inter, sans-serif" }}>
                    <span className="flex items-center gap-2">
                      <Truck size={14} className="text-[#C8A96A]" />
                      {cart.shipping === 0 ? "Free shipping unlocked" : `${eur(cart.freeShippingRemaining)} to free shipping`}
                    </span>
                    <span className="text-[#0C111C]/50">{progressPct}%</span>
                  </div>
                  <div className="relative h-1 bg-[#0C111C]/8">
                    <motion.div
                      initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-y-0 left-0 bg-[#C8A96A]"
                    />
                  </div>
                </div>

                <div className="mt-10 border-t border-[#0C111C]/10">
                  <AnimatePresence>
                    {cart.items.map((item) => (
                      <LineItem key={item.id} item={item} />
                    ))}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>

          {/* RIGHT — summary sticky */}
          {cart.items.length > 0 && (
            <aside className="lg:sticky lg:top-28 lg:self-start" data-testid="cart-summary">
              <div className="bg-[#EDE2CE]/60 border border-[#0C111C]/10 p-6 md:p-8">
                <Eyebrow>Order summary</Eyebrow>

                <div className="mt-6 space-y-3 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>
                  <div className="flex justify-between">
                    <span className="text-[#3A3A3E]">Subtotal</span>
                    <span className="text-[#0C111C]" data-testid="cart-subtotal">{eur(cart.subtotal)}</span>
                  </div>
                  {cart.discount > 0 && (
                    <div className="flex justify-between text-[#C8A96A]" data-testid="cart-discount-row">
                      <span>Discount · YABA10</span>
                      <span>-{eur(cart.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#3A3A3E]">Shipping</span>
                    <span data-testid="cart-shipping">{cart.shipping === 0 ? "Free" : eur(cart.shipping)}</span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-[#0C111C]/12 flex items-baseline justify-between">
                  <span className="text-lg tracking-[0.1em] uppercase" style={{ fontFamily: "Inter, sans-serif" }}>Total</span>
                  <span className="text-2xl md:text-3xl" data-testid="cart-total" style={{ fontFamily: "Fraunces, serif" }}>
                    {eur(cart.total)}
                  </span>
                </div>

                {/* Promo */}
                <div className="mt-6">
                  <label className="text-[10px] tracking-[0.28em] uppercase text-[#0C111C]/70"
                    style={{ fontFamily: "Inter, sans-serif" }}>
                    Promo code
                  </label>
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); if (error) setError(""); }}
                      onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                      placeholder="YABA10"
                      data-testid="cart-promo-input"
                      className="flex-1 bg-transparent border border-[#0C111C]/25 focus:border-[#0C182E] outline-none px-3 py-2 text-sm tracking-[0.15em] uppercase"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    />
                    <button type="button" onClick={applyPromo} data-testid="cart-promo-apply"
                      className="px-4 border border-[#0C182E] text-[#0C182E] hover:bg-[#0C182E] hover:text-[#F4EDE2] text-[11px] tracking-[0.22em] uppercase transition-colors"
                      style={{ fontFamily: "Inter, sans-serif" }}>
                      Apply
                    </button>
                  </div>
                  {cart.promoCode && !error && (
                    <p className="mt-2 text-xs text-[#C8A96A] flex items-center gap-1" data-testid="cart-promo-applied"
                      style={{ fontFamily: "Inter, sans-serif" }}>
                      <Check size={12} /> {cart.promoCode} applied — 10% off
                    </p>
                  )}
                  {error && (
                    <p className="mt-2 text-xs text-[#B95841] flex items-center gap-1" data-testid="cart-promo-error"
                      style={{ fontFamily: "Inter, sans-serif" }}>
                      <X size={12} /> {error}
                    </p>
                  )}
                </div>

                {/* Checkout CTA */}
                <button
                  type="button"
                  onClick={checkout}
                  disabled={checkingOut}
                  data-testid="cart-checkout"
                  className="mt-6 w-full bg-[#0C182E] hover:bg-[#0C111C] disabled:opacity-70 text-[#F4EDE2] h-14 text-sm tracking-[0.22em] uppercase flex items-center justify-center gap-2 transition-colors"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <Lock size={14} />
                  {checkingOut ? "Redirecting to Stripe…" : "Secure checkout"}
                </button>
                {error && !error.includes("valid") && (
                  <p className="mt-3 text-xs text-[#B95841]" data-testid="cart-checkout-error">{error}</p>
                )}

                <ul className="mt-6 space-y-2 text-xs text-[#3A3A3E]" style={{ fontFamily: "Inter, sans-serif" }}>
                  <li className="flex items-center gap-2"><Truck size={12} className="text-[#C8A96A]" /> Free EU shipping over €200</li>
                  <li className="flex items-center gap-2"><RotateCcw size={12} className="text-[#C8A96A]" /> 30-day easy returns</li>
                  <li className="flex items-center gap-2"><Gift size={12} className="text-[#C8A96A]" /> Every order arrives gift-ready</li>
                </ul>
              </div>
            </aside>
          )}
        </div>

        {/* Recommendations */}
        {cart.items.length > 0 && related.length > 0 && (
          <div className="mt-24" data-testid="cart-recommendations">
            <Eyebrow>You might also like</Eyebrow>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {related.map((p) => (
                <Link key={p.slug} to={`/product/${p.slug}`}
                  data-testid={`cart-related-${p.slug}`}
                  className="group block">
                  <div className="aspect-[558/1000] overflow-hidden bg-[#EDE2CE]">
                    <img src={p.image} alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]" />
                  </div>
                  <div className="mt-4 flex items-baseline justify-between">
                    <div>
                      <p className="text-lg" style={{ fontFamily: "Fraunces, serif" }}>{p.name}</p>
                      <p className="text-sm text-[#4A3626]/80" style={{ fontFamily: "Inter, sans-serif" }}>{p.print}</p>
                    </div>
                    <p className="text-lg text-[#4A3626]" style={{ fontFamily: "Fraunces, serif" }}>{p.price}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
